import { MY_ACCOUNT_SHOPPING_LISTS_URL } from 'features/my-account/routes.commerce';
import { MY_ACCOUNT_LOGIN_URL } from 'features/my-account/routes.roc';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Localizer } from 'services/Localizer';
import { getUserData } from 'services/UserData';
import { BulkAddToListResponse } from 'services/UserData.Commerce';
import Modal from 'shared/components/Modal';
import Spinner from 'shared/components/Spinner';
import CheckCircleSVG from 'shared/components/svg/CheckCircleSVG';
import InlineAlert from 'shared/edit/InlineAlert';
import { toKebabCase } from 'util/StringUtil';
import ShoppingListEditItem from '../my-account/shopping-lists/ShoppingListEditItem';
import { ItemToAddToList, PrivacyType, PrivacyTypeOptions, ShoppingListDto } from '../my-account/shopping-lists/types';
import { useShoppingListLoader } from '../my-account/shopping-lists/useShoppingListLoader';
import { useShoppingLists } from '../my-account/shopping-lists/useShoppingLists';

interface BulkAddToListProps {
	/**
	 * defines if the current user is signed in.
	 */
	isUserSignedIn: boolean;
	/**
	 * Represents the list of products to add to list.
	 */
	itemsToAddToList: ItemToAddToList[];
	/**
	 * Function to clear selected items after adding them to list.
	 */
	clearSelectedItems: () => void;
}

/**
 * Represents the component on a product details page that adds multiple products to the user's shopping list.
 */
export default function BulkAddToList(props: BulkAddToListProps) {
	const [openListModal, setOpenListModal] = useState<boolean>(false);
	const [modalOpen, setModalOpen] = useState(false);
	const [addToListResponse, setAddToListResponse] = useState<BulkAddToListResponse | null>(null);
	const [confirmationPopupOpen, setConfirmationPopupOpen] = useState(false);
	const [addingToList, setAddingToList] = useState(false);

	const triggerRef = useRef<HTMLButtonElement | null>(null);

	const { results } = useShoppingListLoader({
		initialPageSize: 15,
	});

	const {
		state: { isSaving, shoppingListBeingEdited, shoppingList, error, errorMessage },
		actor: { stopEditingShoppingList, saveShoppingList, beginCreateShoppingList, bulkAddProductToList },
	} = useShoppingLists();

	const cancelEditShoppingList = () => {
		stopEditingShoppingList();
		setModalOpen(false);
	};

	const onSaveShoppingList = async (formValues: ShoppingListDto) => {
		const validationResult = await saveShoppingList(formValues);
		if (validationResult) {
			return validationResult;
		} else {
			setModalOpen(false);
			return;
		}
	};

	const handleListModal = async () => {
		if (props.isUserSignedIn) {
			if (openListModal) {
				setOpenListModal(false);
			} else {
				setOpenListModal(true);
			}
		} else {
			window.location.href = MY_ACCOUNT_LOGIN_URL + '?ReturnUrl=' + encodeURIComponent(window.location.pathname);
		}
	};

	const handleAddToList = useCallback(
		async (shoppingListId) => {
			setConfirmationPopupOpen(false);

			try {
				setOpenListModal(false);
				setAddingToList(true);

				const response = await bulkAddProductToList({
					shoppingListId: shoppingListId,
					items: props.itemsToAddToList,
				});

				setConfirmationPopupOpen(true);

				if (!response) {
					setAddToListResponse(null);
					return;
				}

				setAddToListResponse(response);
				if (props.clearSelectedItems) {
					props.clearSelectedItems();
				}
			} catch (error) {
				setAddToListResponse(null);
			}

			setAddingToList(false);
		},
		[props, bulkAddProductToList],
	);

	useEffect(() => {
		(async () => {
			try {
				const data = (await getUserData()).data;

				if (!data?.summary?.organization) {
					const organizationOption = PrivacyTypeOptions.find((s) => s.text === PrivacyType.Organization);

					if (organizationOption != undefined) {
						PrivacyTypeOptions.splice(PrivacyTypeOptions.indexOf(organizationOption));
					}
				}
			} catch (error) {
				console.error('Unable to load user data.', error);
			}
		})();

		if (!shoppingListBeingEdited) {
			beginCreateShoppingList();
		}
	}, [shoppingListBeingEdited, beginCreateShoppingList]);

	useEffect(() => {
		if (shoppingList) {
			results?.results.push(shoppingList);
			handleAddToList(shoppingList.id);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [shoppingList, results]);

	return (
		<>
			<button
				onClick={() => handleListModal()}
				type="button"
				className="roc-btn roc-btn--big"
				disabled={
					addingToList || !props.itemsToAddToList || props.itemsToAddToList.every((e) => e.quantity == null)
				}
				data-testid="bulk-add-to-list-button"
				ref={triggerRef}
			>
				{Localizer('AddToList')}
				{addingToList && <Spinner />}
			</button>

			{addToListResponse !== null ? (
				<Modal
					isOpen={confirmationPopupOpen}
					renderTitle={() => Localizer('AddedToList')}
					closeModal={() => setConfirmationPopupOpen(false)}
					titleIcon={<CheckCircleSVG />}
				>
					<BulkAddedToListAlert response={addToListResponse} setPopupOpen={setConfirmationPopupOpen} />
				</Modal>
			) : null}

			{openListModal && (
				<Modal
					isOpen={openListModal}
					renderTitle={() => Localizer('ShoppingLists')}
					closeModal={() => setOpenListModal(false)}
				>
					<ul className="roc-utlity-nav__menu">
						{results &&
							results.results &&
							results.results.map((shoppingList, index) => {
								return (
									<li className="roc-utlity-nav__menu-item" key={index}>
										<button
											className="roc-utlity-nav__menu-item-link"
											data-testid={`shopping-list-${toKebabCase(shoppingList.name)}-${index}`}
											onClick={() => handleAddToList(shoppingList.id)}
										>
											{shoppingList.name}
										</button>
									</li>
								);
							})}

						<li className="roc-utlity-nav__menu-item">
							<button
								className="roc-utlity-nav__menu-item-link"
								data-testid="add-new-shopping-list"
								onClick={() => (setModalOpen(true), setOpenListModal(false))}
							>
								+ {Localizer('AddNewShoppingList')}
							</button>
						</li>
					</ul>
				</Modal>
			)}

			<Modal
				isOpen={modalOpen}
				renderTitle={() => Localizer('AddNewShoppingList')}
				closeModal={() => setModalOpen(false)}
			>
				<ShoppingListEditItem
					stopEditingShoppingList={cancelEditShoppingList}
					shoppingListBeingEdited={shoppingListBeingEdited || ({} as ShoppingListDto)}
					onSaveShoppingList={onSaveShoppingList}
					isSaving={isSaving}
					shoppingList={[]}
				/>
			</Modal>

			{error != null || errorMessage ? <InlineAlert theme="danger" message={errorMessage} error={error} /> : null}
		</>
	);
}

interface BulkAddedToListAlertProps {
	response: BulkAddToListResponse;
	setPopupOpen: (value: boolean) => void;
}

/**
 * Displays Added to List confirmation for bulk add to list
 */
function BulkAddedToListAlert(props: BulkAddedToListAlertProps) {
	const { response } = props;
	const [displayAlert, setDisplayAlert] = useState(true);

	useEffect(() => {
		setDisplayAlert(true);
	}, [response]);

	if (!displayAlert) {
		return null;
	}

	return (
		<div>
			{response && response.errorMessages && (
				<div className="roc-icon-text roc-toggle-form__error">
					<div data-testid="inline-alert-window" className="roc-alert roc-alert-warning roc-alert-full-width">
						<ul>
							{response.errorMessages.map((message, index) => (
								<li data-testid="inline-alert-text" key={index}>
									<span dangerouslySetInnerHTML={{ __html: message }} />
								</li>
							))}
						</ul>
					</div>
				</div>
			)}

			<div className="roc-item-summary">
				{response &&
					response.items &&
					response.items.map((item, index) => {
						return (
							<div className="roc-item-summary__section" key={index}>
								<div className="roc-item-summary__item">
									<div className="roc-item-summary__image-wrapper">
										{item.imageUrl ? (
											<img
												className="roc-item-summary__image"
												alt={item.name}
												src={item.imageUrl}
											/>
										) : (
											<img
												className="roc-item-summary__image"
												alt={item.name}
												src="/images/image-not-found-medium.png"
											/>
										)}
									</div>
									<div className="roc-item-summary__text">
										<p>
											<strong>{item.name}</strong>
										</p>
										<p>
											{/* #region HCL - replace SKU to ItemNumber */}
											{Localizer('ItemNumber')}:{/* #endregion */}
											<span data-testid="added-to-cart-sku">{item.sku}</span>
										</p>

										<table className="roc-item-summary__attributes">
											<tbody>
												<tr className="roc-item-summary__row">
													<td className="roc-item-summary__col roc-item-summary__col--property">
														<strong>{Localizer('Qty')} </strong>
													</td>

													<td className="roc-item-summary__col roc-item-summary__col--value">
														{item.quantity}
													</td>
												</tr>
											</tbody>
										</table>
									</div>
								</div>
							</div>
						);
					})}
				<div className="roc-item-summary__section">
					<div className="roc-item-summary__btn-row">
						<div className="roc-item-summary__btn-col">
							<button
								type="button"
								onClick={() => props.setPopupOpen(false)}
								className="roc-btn-secondary roc-btn-secondary--big roc-btn-secondary--block roc-item-summary__btn"
								data-testid="continue-shopping-btn"
							>
								{Localizer('ContinueShopping')}
							</button>
						</div>
						<div className="roc-item-summary__btn-col">
							<button
								type="button"
								onClick={() =>
									(window.location.href = `${MY_ACCOUNT_SHOPPING_LISTS_URL}/${response.shoppingListId}`)
								}
								className="roc-btn roc-btn--big roc-btn--block roc-item-summary__btn"
								data-testid="view-list-btn"
							>
								<span className="roc-item-summary__btn-text">{Localizer('ViewList')}</span>
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
